import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';

export const WhatsAppButton = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  const whatsappNumber = '27752781104';
  const message = encodeURIComponent('Hi! I\'m interested in your upholstery services.');
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${message}`;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          className="fixed bottom-6 left-6 z-50"
        >
          <div className="relative">
            {/* Tooltip */}
            <AnimatePresence>
              {showTooltip && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="absolute left-full ml-3 bottom-1/2 translate-y-1/2 whitespace-nowrap"
                >
                  <div className="bg-white rounded-lg shadow-lg px-4 py-2 text-sm font-body text-leather">
                    Chat with us!
                    <div className="absolute right-full top-1/2 -translate-y-1/2 border-8 border-transparent border-r-white"></div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* WhatsApp Button */}
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-button"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              className="whatsapp-btn w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#25D366] flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-110 border-2 border-gold"
            >
              <i className="fab fa-whatsapp text-white text-2xl md:text-3xl"></i>
            </a>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a delay
      setTimeout(() => setShowPrompt(true), 5000);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowPrompt(false);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
  };

  return (
    <AnimatePresence>
      {showPrompt && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-24 left-4 right-4 md:left-auto md:right-6 md:w-96 z-50"
        >
          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gold/20">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-leather flex items-center justify-center flex-shrink-0">
                <i className="fas fa-couch text-gold text-xl"></i>
              </div>
              <div className="flex-1">
                <h4 className="font-heading font-semibold text-leather mb-1">
                  Install Last Upholstery
                </h4>
                <p className="text-sm text-leather/60 font-body mb-4">
                  Add to your home screen for quick access to our services
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={handleInstall}
                    data-testid="pwa-install-btn"
                    className="btn-gold rounded-full px-4 py-2 text-sm"
                  >
                    Install App
                  </Button>
                  <Button
                    onClick={handleDismiss}
                    variant="ghost"
                    data-testid="pwa-dismiss-btn"
                    className="rounded-full px-4 py-2 text-sm text-leather/60"
                  >
                    Not Now
                  </Button>
                </div>
              </div>
              <button
                onClick={handleDismiss}
                className="text-leather/40 hover:text-leather transition-colors"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WhatsAppButton;
